import {Article} from '@/model/Article';
import {SeoScoreMetricName, SeoScoreStatus} from '@/model/SeoScore';
import {evaluateAiVisibility} from '@/service/geoEatScore';
import {hasEatAuthor} from '@/service/extractEatAttribution';
import {EventBus} from '@/utils/EventBus';
import {Store} from '@/plugins/Store';
import {richContentIncludesPermutation} from '@/plugins/SeoScore';
import {removeDiacritics, stripTagsAndEntities} from '@/utils/string';

export type LucyQuestionId = 'next' | 'aiVisibility' | 'redList' | 'cite';

export type LucyPanelPayload = {
    metric?: SeoScoreMetricName;
    question?: LucyQuestionId;
};

export function firstAiVisibilityGap(article: Article): SeoScoreMetricName | null {
    const report = evaluateAiVisibility(article);
    const ok: Record<string, boolean> = {};
    report.factors.forEach((factor) => {
        ok[factor.id] = factor.ok;
    });
    if (!ok.faq) {
        return 'faqPresent';
    }
    if (!ok.schema) {
        return 'schemaMarkup';
    }
    if (!hasEatAuthor(article.eatAttribution)) {
        return 'eatAuthor';
    }
    if (!ok.answers) {
        return 'keywordInIntroduction';
    }
    if (!ok.outbound) {
        return 'outboundLinks';
    }
    if (!ok.facts) {
        return 'paragraphLength';
    }
    if (!ok.freshness) {
        return 'eatDate';
    }
    return null;
}

export function openLucyPanel(payload: LucyPanelPayload = {}): void {
    if (Store.getters.sharePreview) {
        return;
    }
    EventBus.$emit('lucy-panel-open', payload);
}

export function closeLucyPanel(): void {
    EventBus.$emit('lucy-panel-close');
}

export type LucyAdvice = {
    body: string;
    goMetric: SeoScoreMetricName | null;
};

type Translate = (key: string, values?: unknown) => unknown;

const keywordPlaceContent: Partial<Record<SeoScoreMetricName, (article: Article) => string>> = {
    keywordInH1: (article) => (article.h1 || []).map((part) => part.h || '').join(' '),
    keywordInH2: (article) => (article.h2 || []).map((part) => part.h || '').join(' '),
    keywordInH3: (article) => (article.h3 || []).map((part) => part.h || '').join(' '),
    keywordInIntroduction: (article) => article.h1?.[0]?.p || '',
};

function keywordPhrases(article: Article): string[] {
    const all = (article.allKeywords || []).filter((item) => item.keyword && item.keyword.trim());
    const checked = all.filter((item) => item.checked);
    return (checked.length ? checked : all).map((item) => item.keyword);
}

export function textAlreadyHasKeyword(content: string, phrases: string[]): boolean {
    if (!content.trim() || !phrases.length) {
        return false;
    }
    if (richContentIncludesPermutation(content, phrases)) {
        return true;
    }
    const haystack = removeDiacritics(stripTagsAndEntities(content).toLowerCase());
    return phrases.some((phrase) => {
        const words = removeDiacritics(phrase.toLowerCase())
            .split(/\s+/)
            .filter((word) => word.length > 1);
        return words.length > 0 && words.every((word) => haystack.includes(word));
    });
}

export function lucyMetricIsDone(
    metric: SeoScoreMetricName,
    status: SeoScoreStatus,
    article: Article
): boolean {
    if (metric === 'aiVisibility') {
        return firstAiVisibilityGap(article) === null;
    }
    if (status === 'GREEN') {
        return true;
    }
    const contentOf = keywordPlaceContent[metric];
    if (contentOf && textAlreadyHasKeyword(contentOf(article), keywordPhrases(article))) {
        return true;
    }
    return false;
}

export function lucyAdviceForMetric(
    metric: SeoScoreMetricName,
    status: SeoScoreStatus,
    article: Article,
    t: Translate,
    te: (key: string) => boolean
): LucyAdvice {
    const text = (key: string, values?: unknown): string => String(t(key, values));

    if (metric === 'aiVisibility') {
        const gap = firstAiVisibilityGap(article);
        if (!gap) {
            return {body: text('lucy.coach.aiVisibility.ok'), goMetric: null};
        }
        return {
            body: text('lucy.coach.aiVisibility.body', [t('seoScore.' + gap + '.label')]),
            goMetric: gap
        };
    }

    if (lucyMetricIsDone(metric, status, article)) {
        return {
            body: text('lucy.done', [t('seoScore.' + metric + '.label')]),
            goMetric: null
        };
    }

    if (metric === 'faqPresent' && article.aiWritingBrief?.questions?.length) {
        const questions = article.aiWritingBrief.questions.slice(0, 3).map((item) => item.question).join('; ');
        return {
            body: text('lucy.coach.faqFromBrief', [questions]),
            goMetric: 'faqPresent'
        };
    }
    if (metric === 'keywordInIntroduction' && article.aiWritingBrief?.answers?.length) {
        return {
            body: text('lucy.coach.introFromBrief'),
            goMetric: 'keywordInIntroduction'
        };
    }

    if (te('lucy.coach.' + metric)) {
        return {body: text('lucy.coach.' + metric), goMetric: metric};
    }
    if (te('seoScore.detail.tips.' + metric)) {
        return {body: text('seoScore.detail.tips.' + metric), goMetric: metric};
    }
    return {body: text('seoScore.' + metric + '.description'), goMetric: metric};
}

export function lucyCiteAdvice(article: Article, t: Translate): LucyAdvice {
    const text = (key: string, values?: unknown): string => String(t(key, values));
    const brief = article.aiWritingBrief;
    if (!brief || (!brief.questions.length && !brief.answers.length && !brief.entities.length)) {
        return {body: text('lucy.coach.citeEmpty'), goMetric: 'faqPresent'};
    }
    const questions = brief.questions.slice(0, 3).map((item) => item.question).join('; ');
    const entities = brief.entities.slice(0, 4).join(', ');
    return {
        body: text('lucy.coach.citeBody', [questions || text('lucy.coach.citeNoQuestions'), entities || text('lucy.coach.citeNoEntities')]),
        goMetric: 'faqPresent'
    };
}
